import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useSettings } from "@/contexts/SettingsContext";
import { subscribeTicker } from "@/lib/binance";
import { TrendingUp, Zap, Wallet as WalletIcon, ShieldCheck, LineChart, Rocket, ArrowRight } from "lucide-react";
import TopBar from "@/components/TopBar";

const TICKER_ASSETS = [
  { symbol: "BTCUSDT", provider: "binance", digits: 2 },
  { symbol: "ETHUSDT", provider: "binance", digits: 2 },
  { symbol: "SOLUSDT", provider: "binance", digits: 2 },
  { symbol: "BNBUSDT", provider: "binance", digits: 2 },
  { symbol: "XRPUSDT", provider: "binance", digits: 4 },
  { symbol: "DOGEUSDT", provider: "binance", digits: 5 },
  { symbol: "EURUSD", provider: "biquote", digits: 5 },
  { symbol: "GBPUSD", provider: "biquote", digits: 5 },
  { symbol: "USDJPY", provider: "biquote", digits: 3 },
  { symbol: "XAUUSD", provider: "biquote", digits: 2 },
];

export default function Landing() {
  const { t } = useI18n();
  const { settings, loading } = useSettings();
  const [ticks, setTicks] = useState({});
  useEffect(() => {
    const ws = subscribeTicker(TICKER_ASSETS, (tk) => setTicks((p) => ({ ...p, [tk.symbol]: tk })));
    return () => { try { ws.close(); } catch {} };
  }, []);

  const features = [
    { icon: Zap, key: "f1" }, { icon: LineChart, key: "f2" }, { icon: Rocket, key: "f3" },
    { icon: ShieldCheck, key: "f4" }, { icon: WalletIcon, key: "f5" }, { icon: TrendingUp, key: "f6" },
  ];

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />

      {/* Ticker marquee */}
      <div className="border-b border-line overflow-hidden bg-panel py-2">
        <div className="marquee flex gap-8 whitespace-nowrap">
          {[...TICKER_ASSETS, ...TICKER_ASSETS].map((a, i) => {
            const tk = ticks[a.symbol];
            const label = a.symbol.endsWith("USDT") ? a.symbol.replace("USDT","/USDT") :
                          (a.symbol.length === 6 ? `${a.symbol.slice(0,3)}/${a.symbol.slice(3)}` : a.symbol);
            return (
              <div key={i} className="flex items-center gap-2 text-xs font-mono">
                <span className="text-muted">{label}</span>
                <span className="text-white">{tk?.price ? tk.price.toFixed(a.digits) : "…"}</span>
                <span className={tk?.change >= 0 ? "text-buy" : "text-sell"}>
                  {tk ? `${tk.change >= 0 ? "+" : ""}${tk.change.toFixed(2)}%` : ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hero */}
      <section className="radial-glow grid-lines relative">
        <div className="max-w-6xl mx-auto px-6 pt-24 pb-32">
          <div className="inline-flex items-center gap-2 border border-line bg-panel px-3 py-1 rounded-full mb-8">
            <span className="w-2 h-2 rounded-full bg-buy animate-pulse"/>
            <span className="text-xs uppercase tracking-widest text-muted">{t("hero_tag")}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter max-w-4xl leading-[1.05]">
            {t("hero_title")}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-muted leading-relaxed">{t("hero_sub")}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-cta-start" className="inline-flex items-center gap-2 bg-buy text-black font-bold px-6 py-3 rounded-sm hover:bg-emerald-400 transition-colors">
              {t("cta_start")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" data-testid="hero-cta-login" className="inline-flex items-center gap-2 border border-line bg-panel text-white px-6 py-3 rounded-sm hover:bg-panel-hover transition-colors">
              {t("cta_login")}
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            {[
              {n:"$10,000", l:"Demo balance"},
              {n:"87%", l:"Max payout"},
              {n:"500x", l:"Max leverage"},
              {n:"<50ms", l:"Latency"},
            ].map((s) => (
              <div key={s.l}>
                <div className="font-mono text-2xl sm:text-3xl font-bold">{s.n}</div>
                <div className="text-[10px] uppercase tracking-widest text-dim mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-line py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <div className="text-[10px] uppercase tracking-widest text-buy mb-3">— {t("live_markets")}</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{t("features_title")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line">
            {features.map(({ icon: Icon, key }) => (
              <div key={key} className="bg-app p-8 hover:bg-panel transition-colors group">
                <Icon className="w-6 h-6 text-buy mb-4" />
                <div className="font-semibold text-lg mb-1">{t(`${key}_t`)}</div>
                <div className="text-sm text-muted leading-relaxed">{t(`${key}_d`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
